import React, { useCallback, useEffect, useState } from 'react'
import { Alert, Box, IconButton, Link, Paper, Stack, Typography } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import ListAltIcon from '@mui/icons-material/ListAlt'
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline'
import DeleteIcon from '@mui/icons-material/Delete'
import DriveFileMoveIcon from '@mui/icons-material/DriveFileMove'
import EditIcon from '@mui/icons-material/Edit'
import PersonRemoveIcon from '@mui/icons-material/PersonRemove'
import PersonOffOutlinedIcon from '@mui/icons-material/PersonOffOutlined'
import { useNavigate, useParams } from 'react-router-dom'
import { AccessLevel, useSessionContext } from 'session'
import {
  AlertDialog,
  CenteredCircularProgress,
  DragAndDropStack,
  ExecutiveOrgAddressEditor,
  ExecutiveOrgDetailsCard,
  ExecutiveOrgUrlEditor,
  IconButtonWithTooltip,
  useGlobalMessageHandler,
  useProtectedRoleHoldersLut,
  useUsPrefixLuts
} from 'components'
import { ExecutiveOrgHierarchyCard } from 'components/us/executives/executive-org-hierarchy-card'
import { ExecutiveOrgChildrenReorderDialog } from 'components/us/executives/executive-org-children-reorder-dialog'
import { CommentDialog } from 'components/common/comment-dialog'
import { formatErrorMessages } from 'interfaces'
import {
  deleteExecutiveOrganization,
  deleteVacantExecutivePosition,
  type ExecOrgDescendantItem,
  type ExecOrgRef,
  type ExecutiveOrganization,
  type ExecutiveOrganizationBasicInfo,
  getExecutiveOrgDescendants,
  getExecutiveOrganization,
  getProtectedRoleHolders,
  reorderPositions,
  retireAllExecutiveOrgPositions,
  type Prefix
} from 'services'
import { type ExecutivePosition } from 'services/person/us/edit/executiveRolesService'
import { filterBuildingsForExecutive, formatFullName, isExecPositionRowVacant, executivePositionsSearchPath } from 'helpers'
import { EXEC_POSITION_STATUSES, isStateExecutiveRole } from 'utils'

const formatPositionCard = (pos: ExecutivePosition, prefixes: Prefix[]): string => {
  const status = EXEC_POSITION_STATUSES[pos.statusId]?.label
  return `${formatFullName(pos.person, prefixes)} — ${pos.title}${status != null ? ` (${status})` : ''}`
}

// Descendants of `rootId` from the flat department list (BFS over parentId) —
// the sub-orgs a cascade delete would remove, for the confirmation dialog.
const collectSubtree = (all: ExecOrgDescendantItem[], rootId: number): ExecOrgRef[] => {
  const byParent = new Map<number, ExecOrgDescendantItem[]>()
  all.forEach(item => {
    if (item.parentId == null) return
    byParent.set(item.parentId, [...(byParent.get(item.parentId) ?? []), item])
  })
  const result: ExecOrgRef[] = []
  const stack = [rootId]
  while (stack.length > 0) {
    const parentId = stack.pop() as number
    for (const child of byParent.get(parentId) ?? []) {
      result.push({ id: child.id, name: child.name, code: child.code })
      stack.push(child.id)
    }
  }
  return result
}

export const ExecutiveOrgDetailPage: React.FC = () => {
  const { execOrgId } = useParams<{ execOrgId: string }>()
  const parsedExecOrgId = execOrgId != null ? parseInt(execOrgId, 10) : 0
  const navigate = useNavigate()
  const { changeModuleName, changeUniqueId, openNewTab, hasAccessLevel } = useSessionContext()
  const { lutValues: usPrefixLutValues } = useUsPrefixLuts()
  const { showErrorMessage, showSuccessMessage } = useGlobalMessageHandler()
  const { setLutValues: setProtectedRoleHoldersLut } = useProtectedRoleHoldersLut()
  const [org, setOrg] = useState<ExecutiveOrganization | null>(null)
  const [loading, setLoading] = useState<boolean>(true)
  const [positionPendingDelete, setPositionPendingDelete] = useState<ExecutivePosition | null>(null)
  const [showRetireAllConfirm, setShowRetireAllConfirm] = useState<boolean>(false)
  const [showDeleteOrgConfirm, setShowDeleteOrgConfirm] = useState<boolean>(false)
  const [subOrgsToDelete, setSubOrgsToDelete] = useState<ExecOrgRef[]>([])
  const [busy, setBusy] = useState<boolean>(false)
  const [contactStatus, setContactStatus] = useState({ hasAddress: true, hasPhone: true, hasWebsite: true })
  const [reorderOpen, setReorderOpen] = useState<boolean>(false)
  const [hierarchyReloadKey, setHierarchyReloadKey] = useState<number>(0)

  // The Address & URL editors report their contact completeness up so the banner
  // can name what's missing (TI-4100). Memoized so the child effects that depend
  // on these callbacks don't re-fire every render.
  const handleContactStatus = useCallback((status: { hasAddress: boolean, hasPhone: boolean }): void => {
    setContactStatus(prev => ({ ...prev, ...status }))
  }, [])
  const handleWebsiteStatus = useCallback((hasWebsite: boolean): void => {
    setContactStatus(prev => ({ ...prev, hasWebsite }))
  }, [])

  useEffect(() => { changeModuleName('Executive Organization') }, [changeModuleName])

  const loadOrg = useCallback((): void => {
    if (Number.isNaN(parsedExecOrgId) || parsedExecOrgId === 0) {
      setLoading(false)
      return
    }
    setLoading(true)
    getExecutiveOrganization(parsedExecOrgId)
      .then(setOrg)
      .catch(() => { showErrorMessage('Failed to load executive organization.') })
      .finally(() => { setLoading(false) })
  }, [parsedExecOrgId, showErrorMessage])

  useEffect(() => { loadOrg() }, [loadOrg])

  useEffect(() => {
    changeUniqueId(org != null ? `${org.id} - ${org.name}` : '')
    if (org != null) document.title = `Exec Org ${org.id} - ${org.name}`
  }, [org, changeUniqueId])

  // Saving the Details card returns the refreshed basic info; merge it into the
  // loaded org so the header (and hierarchy root label) reflect the new name.
  const handleOrgUpdated = (updated: ExecutiveOrganizationBasicInfo): void => {
    setOrg(prev => (prev != null ? { ...prev, ...updated } : prev))
  }

  const confirmDelete = (): void => {
    if (positionPendingDelete?.id == null) {
      setPositionPendingDelete(null)
      return
    }
    const positionId = positionPendingDelete.id
    deleteVacantExecutivePosition(positionId).then(response => {
      if (response.succeeded) {
        showSuccessMessage('Vacant position deleted.')
        loadOrg()
      } else {
        showErrorMessage(response.message ?? 'Failed to delete position.')
      }
    }).catch(() => {
      showErrorMessage('Failed to delete position.')
    }).finally(() => {
      setPositionPendingDelete(null)
    })
  }

  const confirmRetireAll = (): void => {
    setShowRetireAllConfirm(false)
    setBusy(true)
    retireAllExecutiveOrgPositions(parsedExecOrgId).then(response => {
      if (response.succeeded) {
        showSuccessMessage(`Retired ${response.result?.retiredCount ?? 0} position(s).`)
        loadOrg()
        getProtectedRoleHolders().then(setProtectedRoleHoldersLut).catch(() => { showErrorMessage('Could not refresh the protected-role holders hint; it may be stale.') })
      } else {
        const message = formatErrorMessages(response.errors)
        showErrorMessage(message !== '' ? message : 'Failed to retire positions.')
      }
    }).catch(() => {
      showErrorMessage('Failed to retire positions.')
    }).finally(() => {
      setBusy(false)
    })
  }

  const confirmDeleteOrg = (): void => {
    setShowDeleteOrgConfirm(false)
    setBusy(true)
    const parentOrgId = org?.parentOrg?.id ?? null
    deleteExecutiveOrganization(parsedExecOrgId, subOrgsToDelete.length > 0).then(response => {
      if (response.succeeded) {
        showSuccessMessage('Organization deleted.')
        navigate(parentOrgId != null ? `/us/executives/orgs/${parentOrgId}` : '/us/executives/search')
      } else if (response.status === 403) {
        showErrorMessage("You don't have permission to delete executive organizations.")
      } else {
        const message = formatErrorMessages(response.errors)
        showErrorMessage(message !== '' ? message : 'Failed to delete organization.')
      }
    }).catch(() => {
      showErrorMessage('Failed to delete organization.')
    }).finally(() => {
      setBusy(false)
    })
  }

  // Fetch the org's subtree first so the confirm dialog can list the sub-orgs a
  // cascade would remove (and confirmDeleteOrg knows whether to pass cascade).
  const openDeleteDialog = (): void => {
    if (org == null) return
    setBusy(true)
    getExecutiveOrgDescendants(org.topOrg.id)
      .then(items => { setSubOrgsToDelete(collectSubtree(items, org.id)) })
      .catch(() => { setSubOrgsToDelete([]) })
      .finally(() => { setBusy(false); setShowDeleteOrgConfirm(true) })
  }

  if (Number.isNaN(parsedExecOrgId) || parsedExecOrgId === 0) {
    return <Box>Invalid Executive Organization ID</Box>
  }
  if (loading) {
    return <CenteredCircularProgress loading={loading} size={100} position='absolute' />
  }
  if (org == null) {
    return <Box>Executive Organization not found.</Box>
  }

  const isManager = hasAccessLevel(AccessLevel.MANAGER)
  const sortedPositions = org.positions.slice().sort((a, b) =>
    a.displayOrder !== b.displayOrder ? a.displayOrder - b.displayOrder : a.id - b.id
  )
  const missingContact = [
    ...(contactStatus.hasAddress ? [] : ['address']),
    ...(contactStatus.hasPhone ? [] : ['phone']),
    ...(contactStatus.hasWebsite ? [] : ['website'])
  ]
  const breadcrumb = org.ancestors.length > 0
    ? org.ancestors
    : (org.topOrg.id !== org.id ? [org.topOrg] : [])

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <Box width='97%' sx={{ pb: 1 }}>
        <Stack direction='row' alignItems='flex-start' justifyContent='space-between' sx={{ mb: 2 }}>
          <Stack spacing={0.5}>
            <Typography variant='h4'>{org.name}</Typography>
            {org.code !== '' && <Typography variant='subtitle2' color='text.secondary'>{org.code}</Typography>}
            {breadcrumb.length > 0 && (
              <Stack spacing={0.25}>
                {breadcrumb.map((ancestor, index) => (
                  <Typography key={ancestor.id} variant='body2' color='text.secondary'>
                    <Box component='span' sx={{ fontWeight: 600 }}>{index === 0 ? 'TOP ORGANIZATION' : 'NEXT LEVEL'}:</Box>{' '}
                    <Link
                      component='button'
                      variant='body2'
                      underline='hover'
                      onClick={() => { openNewTab(`/us/executives/orgs/${ancestor.id}`) }}
                    >
                      {ancestor.name}
                    </Link>
                  </Typography>
                ))}
                <Typography variant='body2' color='text.secondary'>
                  <Box component='span' sx={{ fontWeight: 600 }}>THIS ORGANIZATION:</Box> {org.name}
                </Typography>
              </Stack>
            )}
          </Stack>
          <Stack direction='row' spacing={0.5}>
            <CommentDialog recordId={org.id} recordType='E' />
            {isManager && (
              <>
                <IconButtonWithTooltip
                  label='Move Organization'
                  color='primary'
                  onClick={() => { openNewTab(`/us/executives/orgs/${parsedExecOrgId}/move`) }}
                  icon={<DriveFileMoveIcon />}
                />
                <IconButtonWithTooltip
                  label='Add Sub-Organization'
                  color='primary'
                  onClick={() => { openNewTab(`/us/executives/orgs/${parsedExecOrgId}/create`) }}
                  icon={<AddIcon />}
                />
                {org.positions.some(pos => !isExecPositionRowVacant(pos.person)) && (
                  <IconButtonWithTooltip
                    label='Retire All Positions'
                    color='primary'
                    disabled={busy}
                    onClick={() => { setShowRetireAllConfirm(true) }}
                    icon={<PersonRemoveIcon />}
                  />
                )}
                <IconButtonWithTooltip
                  label='Delete Organization'
                  sx={{ color: 'error.main' }}
                  disabled={busy}
                  onClick={() => { openDeleteDialog() }}
                  icon={<DeleteIcon />}
                />
              </>
            )}
          </Stack>
        </Stack>
        {isManager && missingContact.length > 0 && (
          <Alert severity='warning' sx={{ mb: 2 }}>
            This organization is missing: {missingContact.join(', ')}. Add the missing contact info to keep the record complete.
          </Alert>
        )}
        <Box sx={{ mb: 2 }}>
          <ExecutiveOrgHierarchyCard
            key={org.id}
            org={org}
            onReorderChildren={hasAccessLevel(AccessLevel.EDITOR) ? () => { setReorderOpen(true) } : undefined}
            reloadToken={hierarchyReloadKey}
          />
        </Box>
        {isManager && (
          <Stack spacing={3} sx={{ mb: 2 }}>
            <ExecutiveOrgDetailsCard
              orgId={parsedExecOrgId}
              name={org.name}
              code={org.code}
              roleId={org.role.id}
              responsibilities={org.responsibilities ?? []}
              canEdit
              onUpdated={handleOrgUpdated}
              onRoleChanged={() => { setHierarchyReloadKey(prev => prev + 1) }}
            />
            <Paper sx={{ p: 2 }}>
              <Typography variant='h6' sx={{ mb: 2 }}>Address &amp; Phone</Typography>
              <ExecutiveOrgAddressEditor
                orgId={parsedExecOrgId}
                parentOrgId={org.parentOrg?.id ?? null}
                parentOrgName={org.parentOrg?.name}
                filterBuildings={(buildings) => filterBuildingsForExecutive(
                  buildings,
                  isStateExecutiveRole(org.role.id),
                  org.topOrg.id,
                  org.state.abbrev
                )}
                onContactStatus={handleContactStatus}
              />
            </Paper>
            <Paper sx={{ p: 2 }}>
              <Typography variant='h6' sx={{ mb: 2 }}>URLs</Typography>
              <ExecutiveOrgUrlEditor orgId={parsedExecOrgId} roleId={org.role.id} onWebsiteStatus={handleWebsiteStatus} />
            </Paper>
          </Stack>
        )}
        <Stack spacing={1}>
          <Stack direction='row' alignItems='center' justifyContent='space-between'>
            <Typography variant='h5'>Positions</Typography>
            <Stack direction='row' spacing={0.5}>
              <IconButtonWithTooltip
                label='View Positions'
                color='primary'
                onClick={() => { openNewTab(executivePositionsSearchPath(org.id, org.topOrg.id)) }}
                icon={<PeopleOutlineIcon />}
              />
              <IconButtonWithTooltip
                label='Bulk Edit Positions'
                color='primary'
                onClick={() => { openNewTab(`/us/executives/orgs/${parsedExecOrgId}/bulk-positions`) }}
                icon={<ListAltIcon />}
              />
              <IconButtonWithTooltip
                label='Add Position'
                color='primary'
                onClick={() => { openNewTab(`/us/executives/orgs/${parsedExecOrgId}/add-position`) }}
                icon={<AddIcon />}
              />
              <IconButtonWithTooltip
                label='Add Vacant Position'
                color='primary'
                onClick={() => { openNewTab(`/us/executives/orgs/${parsedExecOrgId}/add-position?vacant=true`) }}
                icon={<PersonOffOutlinedIcon />}
              />
            </Stack>
          </Stack>
          {sortedPositions.length === 0
            ? <Box>No positions in this organization.</Box>
            : (
              <DragAndDropStack
                id={`exec-org-positions-${parsedExecOrgId}`}
                originalItems={sortedPositions}
                disableDrag={!hasAccessLevel(AccessLevel.EDITOR)}
                makeGray={false}
                updateDisplayOrder={async (items: ExecutivePosition[]) => {
                  const response = await reorderPositions(parsedExecOrgId, items.map(item => item.id))
                  if (response.succeeded) {
                    setOrg(prev => (prev != null
                      ? { ...prev, positions: items.map((item, position) => ({ ...item, displayOrder: position + 1 })) }
                      : prev))
                  } else {
                    // The save failed, so the persisted order is unchanged.
                    // Refetch (without the full-page spinner) to hand the list a
                    // fresh originalItems reference, snapping it back to the saved
                    // order instead of leaving the rejected one on screen.
                    getExecutiveOrganization(parsedExecOrgId).then(setOrg).catch(() => {})
                  }
                  return { ...response, result: items }
                }}
                itemRenderer={(pos: ExecutivePosition, index: number) => (
                  <Paper id={`exec-pos-${pos.id}`} sx={{ p: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography>{index + 1}. {formatPositionCard(pos, usPrefixLutValues.prefixes)}</Typography>
                    <Stack direction='row' spacing={0.5}>
                      <IconButton
                        aria-label='Edit position'
                        size='small'
                        onClick={() => { openNewTab(`/us/executives/position-update/${pos.id}`) }}
                      >
                        <EditIcon fontSize='small' />
                      </IconButton>
                      {isExecPositionRowVacant(pos.person) && (
                        <IconButton
                          aria-label='Delete vacant position'
                          size='small'
                          color='error'
                          onClick={() => { setPositionPendingDelete(pos) }}
                        >
                          <DeleteIcon fontSize='small' />
                        </IconButton>
                      )}
                    </Stack>
                  </Paper>
                )}
              />
              )}
        </Stack>
        <AlertDialog
          open={positionPendingDelete != null}
          title='Delete Vacant Position'
          contentText={`This will permanently delete the vacant position "${positionPendingDelete?.title ?? ''}" and renumber the remaining positions in this organization. This cannot be undone. Are you sure?`}
          nameButtonConfirm='Delete'
          nameButtonCancel='Cancel'
          onButtonClick={confirmDelete}
          onClose={() => { setPositionPendingDelete(null) }}
          containedButton
        />
        <AlertDialog
          open={showRetireAllConfirm}
          title='Retire All Positions'
          contentText={`This will retire every active position in "${org.name}", marking each as Vacant. This cannot be undone. Continue?`}
          nameButtonConfirm='Retire All'
          nameButtonCancel='Cancel'
          onButtonClick={confirmRetireAll}
          onClose={() => { setShowRetireAllConfirm(false) }}
          containedButton
        />
        <AlertDialog
          open={showDeleteOrgConfirm}
          title='Delete Organization'
          contentText={subOrgsToDelete.length > 0
            ? `This will permanently delete "${org.name}" AND its ${subOrgsToDelete.length} sub-organization(s): ${subOrgsToDelete.map(subOrg => subOrg.name).join(', ')}. Deletion is refused if any position in the subtree is still filled. Removal from Salesforce may take up to ~2 days. This cannot be undone. Are you sure?`
            : `This will permanently delete "${org.name}" and renumber its sibling organizations. Deletion is refused if any position is still filled. Removal from Salesforce may take up to ~2 days. This cannot be undone. Are you sure?`}
          nameButtonConfirm={subOrgsToDelete.length > 0 ? 'Delete All' : 'Delete'}
          nameButtonCancel='Cancel'
          onButtonClick={confirmDeleteOrg}
          onClose={() => { setShowDeleteOrgConfirm(false) }}
          containedButton
        />
        <ExecutiveOrgChildrenReorderDialog
          open={reorderOpen}
          onClose={() => { setReorderOpen(false) }}
          parentOrgId={parsedExecOrgId}
          parentName={org.name}
          onReordered={() => { setHierarchyReloadKey(prev => prev + 1) }}
        />
      </Box>
    </Box>
  )
}
